import t from "@/lang/lang";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { UUID } from "@bitory-gmbh/nidavellir";
import { TextInputField } from "@/shared/components/field/input-field";
import {
  updateAhuDataCache,
  useHtzAirHandlingUnitDataPutMutation,
  useHtzAirHandlingUnitDataShowQuery,
} from "../api/ahu-data-api";
import { useOptimisticDebouncedMutation } from "@/shared/lib/debounce/use-optimistic-debounced-mutation";
import { ErrValidationFailedContextProvider } from "@/shared/lib/errors/err-validation-failed-context";
import { cn } from "@/shared/lib/utils";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { CardSkeleton } from "@/shared/components/card-skeleton";

export function AhuDataBaseDataCard({
  id,
  hideLabel,
  disabled,
}: {
  id: UUID;
  hideLabel?: boolean;
  disabled?: boolean;
}) {
  const { data, error, isLoading } = useHtzAirHandlingUnitDataShowQuery({ id });

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  if (isLoading) {
    return <CardSkeleton />;
  }

  // we make sure that the child will get a value from the RTK Query Cache
  if (!data) return;

  // The air handling unit data is an entity of its own, also where it is
  // shown inside a document, e.g. a report. Its errors must not mix with
  // the errors of the document.
  return (
    <ErrValidationFailedContextProvider>
      <AhuDataBaseDataCardContent
        id={id}
        disabled={disabled}
        hideLabel={hideLabel}
      />
    </ErrValidationFailedContextProvider>
  );
}

function AhuDataBaseDataCardContent({
  id,
  hideLabel,
  disabled,
}: {
  id: UUID;
  hideLabel?: boolean;
  disabled?: boolean;
}) {
  const { data } = useHtzAirHandlingUnitDataShowQuery({ id });
  const [set] = useOptimisticDebouncedMutation(
    data!,
    updateAhuDataCache,
    useHtzAirHandlingUnitDataPutMutation(),
    { resetOnError: false },
  );

  if (!data) return;

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {t("RLT-Anlage: ")}
          {data.designation}
        </CardTitle>
      </CardHeader>
      <CardContent className="@container">
        <div className="grid gap-3 @md:grid-cols-2">
          <TextInputField
            label={t("Label")}
            id="label"
            text={data.label}
            onTextChange={(label) => set({ ...data, label })}
            fieldName="label"
            disabled={disabled}
            fieldProps={{ className: cn(hideLabel && "hidden") }}
          />
          <TextInputField
            label={t("Bezeichnung")}
            id="designation"
            text={data.designation}
            onTextChange={(designation) => set({ ...data, designation })}
            fieldName="designation"
            disabled={disabled}
          />
          <TextInputField
            label={t("Standort")}
            id="location"
            text={data.location}
            onTextChange={(location) => set({ ...data, location })}
            fieldName="location"
            disabled={disabled}
          />
          <TextInputField
            label={t("Baujahr")}
            id="constructionYear"
            text={data.constructionYear}
            onTextChange={(constructionYear) =>
              set({ ...data, constructionYear })
            }
            fieldName="constructionYear"
            disabled={disabled}
          />
          <TextInputField
            label={t("Luftstrom m³/h")}
            id="volumeFlowRate"
            text={data.volumeFlowRate}
            onTextChange={(volumeFlowRate) => set({ ...data, volumeFlowRate })}
            fieldName="volumeFlowRate"
            disabled={disabled}
          />
        </div>
      </CardContent>
    </Card>
  );
}
